import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaMapMarkerAlt, FaStore, FaTable } from 'react-icons/fa';
import { authAPI } from '../utils/api';
import { getInviteSession, clearInviteSession } from './SignupProtectedRoute';
import './Auth.css';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};

  if (!form.restaurantName.trim()) errors.restaurantName = 'Restaurant name is required';
  if (!form.location.trim()) errors.location = 'Location is required';

  const tables = Number(form.numberOfTables);
  if (!form.numberOfTables) {
    errors.numberOfTables = 'Number of tables is required';
  } else if (!Number.isFinite(tables) || !Number.isInteger(tables) || tables <= 0 || tables > 500) {
    errors.numberOfTables = 'Enter a valid table count (1-500)';
  }

  if (!form.adminEmail.trim()) errors.adminEmail = 'Email is required';
  else if (!emailRegex.test(form.adminEmail.trim())) errors.adminEmail = 'Enter a valid email';

  if (!form.password) errors.password = 'Password is required';
  else if (form.password.length < 8) errors.password = 'Password must be at least 8 characters';

  if (!form.confirmPassword) errors.confirmPassword = 'Confirm password is required';
  else if (form.confirmPassword !== form.password) errors.confirmPassword = 'Passwords do not match';

  return errors;
}

function Signup() {
  const navigate = useNavigate();
  const inviteSession = useMemo(() => getInviteSession(), []);

  const [formData, setFormData] = useState({
    restaurantName: '',
    location: '',
    numberOfTables: '',
    adminEmail: '',
    password: '',
    confirmPassword: '',
  });
  
  // Convert form data to snake_case for API
  const getApiPayload = () => ({
    restaurant_name: formData.restaurantName.trim(),
    location: formData.location.trim(),
    number_of_tables: Number(formData.numberOfTables),
    admin_email: formData.adminEmail.trim().toLowerCase(),
    password: formData.password,
    signup_invite_token: inviteSession?.inviteToken
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
    setFieldErrors((p) => ({ ...p, [e.target.name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = validate(formData);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    if (!inviteSession?.inviteToken) {
      // Should not happen because of SignupProtectedRoute, but keep it safe
      navigate('/signup-access', { replace: true });
      return;
    }

    setLoading(true);
    try {
      const payload = getApiPayload();
      console.log('Submitting form with data:', payload);

      const response = await authAPI.restrictedSignup(payload);

      if (response?.success) {
        // Clear invite gate session after successful signup
        clearInviteSession();
        navigate('/dashboard');
      } else {
        setError(response?.message || 'Signup failed');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Create Restaurant Admin</h1>
          <p>Complete signup to create your restaurant and admin account.</p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="restaurantName">
              <FaStore /> Restaurant Name
            </label>
            <input
              id="restaurantName"
              name="restaurantName"
              value={formData.restaurantName}
              onChange={handleChange}
              placeholder="e.g. Shan's Kitchen"
              required
            />
            {fieldErrors.restaurantName && <div className="field-error">{fieldErrors.restaurantName}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="location">
              <FaMapMarkerAlt /> Location
            </label>
            <input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="City / Area"
              required
            />
            {fieldErrors.location && <div className="field-error">{fieldErrors.location}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="numberOfTables">
              <FaTable /> Number of Tables
            </label>
            <input
              id="numberOfTables"
              name="numberOfTables"
              value={formData.numberOfTables}
              onChange={handleChange}
              placeholder="e.g. 12"
              required
              inputMode="numeric"
            />
            {fieldErrors.numberOfTables && <div className="field-error">{fieldErrors.numberOfTables}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="adminEmail">
              <FaEnvelope /> Admin Email
            </label>
            <input
              type="email"
              id="adminEmail"
              name="adminEmail"
              value={formData.adminEmail}
              onChange={handleChange}
              placeholder="owner@restaurant.com"
              required
              autoComplete="email"
            />
            {fieldErrors.adminEmail && <div className="field-error">{fieldErrors.adminEmail}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">
                <FaLock /> Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 8 characters"
                required
                autoComplete="new-password"
              />
              {fieldErrors.password && <div className="field-error">{fieldErrors.password}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                <FaLock /> Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter password"
                required
                autoComplete="new-password"
              />
              {fieldErrors.confirmPassword && <div className="field-error">{fieldErrors.confirmPassword}</div>}
            </div>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="auth-footer">
            <p>
              After signup you will be logged in automatically.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Signup;
